import { getPlaceholders } from 'constants/text/placeholders';

export const getStringFromList = (list: any[] | null) => {
  const { NOT_INDICATED } = getPlaceholders();
  return list && list.length
    ? list
        .map((el: any) =>
          typeof el === 'string' ? el : el.name || el.display_name
        )
        .join(', ')
    : NOT_INDICATED;
};

export const splitToColumns = (list: any[]) => {
  const columns: any = [[], []];
  list.forEach((el: any, i) => {
    const col = i % 2;
    columns[col].push(el);
  });
  return columns;
};

export const getNameByVal = (
  list: any[],
  value: number | number[] | null,
  zeroValue = false
) => {
  // const { NOT_INDICATED } = getPlaceholders();
  const NOT_INDICATED = 'Not indicated';
  if (value || (zeroValue && value === 0)) {
    if (Array.isArray(value)) {
      return (
        list
          .filter((el) => {
            const indicator = el.value || el.id;
            return value.includes(indicator);
          })
          .map((el) => el.label || el.name)
          .join(', ') || NOT_INDICATED
      );
    }

    const currentOption = list.find((el: any) => {
      const indicator = el.value || el.id;
      return indicator === value;
    });
    return currentOption.label || currentOption.name || currentOption.network;
  }
  return NOT_INDICATED;
};

export const getMultiNameByVal = (list: any[], values: number[]) => {
  const { NOT_INDICATED } = getPlaceholders();
  return values.length
    ? list
        .filter((el: any) => values.includes(el.value))
        .map((el: any) => el.label)
        .join(', ')
    : NOT_INDICATED;
};

export const getNameFormObj = (obj: any) => {
  const { NOT_INDICATED } = getPlaceholders();
  return obj && obj.name ? obj.name : NOT_INDICATED;
};

export const getCheckedString = (str: any, maxSize: number | null = null) => {
  const { NOT_INDICATED } = getPlaceholders();
  if (str && maxSize && str.length > maxSize) {
    return `${str.substring(0, maxSize)}...`;
  }
  return str || NOT_INDICATED;
};

export const getTimePeriod = (start: string, end: string) => {
  const { NOW, NOT_INDICATED } = getPlaceholders();
  return start ? `${start} — ${end || NOW}` : NOT_INDICATED;
};

export const getComparedData = (candidateComp: any[], profileComp: any[]) => {
  if (!candidateComp?.length || !profileComp?.length) return [];

  // Create a direct mapping of competency_id to candidate competency
  const candidateCompetencyMap: Record<string, any> = {};

  // Index all candidate competencies by their competency_id for direct lookup
  candidateComp.forEach((comp) => {
    if (comp && comp.competency_id) {
      candidateCompetencyMap[comp.competency_id] = comp;
    }
  });

  // Also create a name-based map as fallback
  const nameMap: Record<string, any> = {};
  candidateComp.forEach((comp) => {
    if (comp?.competency?.name) {
      const normalizedName = comp.competency.name.toLowerCase().trim();
      nameMap[normalizedName] = comp;
    }
  });

  // For each profile competency, find the matching candidate competency by ID
  const result = profileComp
    .map((profileItem) => {
      const competencyId = profileItem?.competency?.id;
      const competencyName = profileItem?.competency?.name;

      let matchedComp = null;

      // Try ID-based match first
      if (competencyId && candidateCompetencyMap[competencyId]) {
        matchedComp = candidateCompetencyMap[competencyId];
      }
      // Try name-based match as fallback
      else if (competencyName) {
        const normalizedName = competencyName.toLowerCase().trim();
        if (nameMap[normalizedName]) {
          matchedComp = nameMap[normalizedName];
        }
      }

      return matchedComp;
    })
    .filter(Boolean); // Remove nulls

  return result;
};

export const getComparedLabel = (candidateComp: any[]) =>
  candidateComp?.map((el: any) => el.competency.name);

export const normalizeCompetencyName = (name: string) => {
  if (!name || typeof name !== 'string') return '';
  // Normalize competency names for more reliable matching
  return name.toLowerCase().trim();
};

/**
 * Extract competency ID from various data formats
 * Supports: { competency: { id } }, { competency: X }, { competency_id: X }
 */
const extractCompetencyId = (el: any): number | undefined => {
  if (el?.competency?.id !== undefined) return el.competency.id;
  if (el?.competency_id !== undefined) return el.competency_id;
  if (typeof el?.competency === 'number') return el.competency;
  return undefined;
};

/**
 * Extract competency name from various data formats
 * Supports: { competency: { name } }
 */
const extractCompetencyName = (el: any): string | undefined => {
  if (typeof el?.competency?.name === 'string') return el.competency.name;
  return undefined;
};

/**
 * Extract score/min_score from various data formats
 * Supports: { min_score: X }, { score: X }
 */
const extractMinScore = (el: any): number => {
  if (el?.min_score !== undefined) return Number(el.min_score) || 0;
  if (el?.score !== undefined) return Number(el.score) || 0;
  return 0;
};

/**
 * Get competency labels and scores from competencies array
 * Handles multiple data formats:
 * - { competency: { id, name }, min_score } - nested object format
 * - { competency: X, min_score } - competency is just an ID
 * - { competency_id: X, score } - flat format with competency_id
 *
 * @param competencies - Array of competency objects in any supported format
 * @param competencyNameLookup - Optional map of competency ID to name for resolving names
 */
export const getCompetenciesScoresAndLabels = (
  competencies: any[],
  competencyNameLookup?: Record<number, string>
) => ({
  competencies,
  competency_labels: competencies?.length
    ? competencies?.map((el: any) => {
        const id = extractCompetencyId(el);
        let name = extractCompetencyName(el);

        // Try to resolve name from lookup if not available
        if (!name && id !== undefined && competencyNameLookup?.[id]) {
          name = competencyNameLookup[id];
        }

        return { name, id };
      })
    : [],
  competency_min_scores: competencies?.length
    ? competencies?.map((el: any) => ({
        min_score: extractMinScore(el),
        id: extractCompetencyId(el)
      }))
    : []
});

export const prepareUpdatedData = (
  method: string,
  prevState: any,
  data: any
) => {
  switch (method) {
    case 'create':
      return {
        ...prevState,
        results: [data, ...prevState.results]
      };
    case 'update':
      return {
        ...prevState,
        results: [
          ...prevState.results.map((el: any) => (el.id === data.id ? data : el))
        ]
      };
    case 'delete':
      return {
        ...prevState,
        results: [...prevState.results.filter((el: any) => el.id !== data.id)]
      };
    default:
      return prevState;
  }
};
